import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Dinero beraETH — intrinsic APR.
 *
 * beraETH (Berachain) is a wrapper over the rberaETH base LST whose ETH
 * rate is synced cross-chain from Dinero's mainnet engine. Its yield is
 * therefore equal to Dinero apxETH's staking APY.
 *
 * Primary source is DefiLlama's apxETH pool (`apy` percent). Fallback:
 * apxETH is a standard ERC-4626 on Ethereum mainnet, so we recover the APR
 * from the growth of `apxETH.convertToAssets(1e18)` over a fixed lookback
 * window, annualised:
 *
 *   apr = (rateNow / rateThen - 1) * (yearSeconds / elapsedSeconds)
 *
 * Read on Ethereum mainnet (chainId 1), where archival state is available
 * through the configured providers. Rotates RPCs and bails (→ 0 via
 * `safeFetch`) if none serve valid historical state. Mirrors the stCELO
 * rate-delta fetcher.
 */

const CHAIN_ID = '1'
/** Dinero apxETH (ERC-4626) on Ethereum mainnet — the rate engine. */
const APXETH: Address = '0x9ba021b0a9b958b5e75ce9f6dff97c7ee52cb3e6'
/** DefiLlama apxETH pool (Ethereum, dinero-(pxeth)) — `apy` percent. */
const APXETH_DEFILLAMA_POOL = 'fc25b5ff-2ba8-44a3-895b-e0d22d96365f'

const ONE_E18 = 10n ** 18n

/** 7-day window — apxETH steps with mainnet validator rewards. */
const WINDOW_SECONDS = 7 * 24 * 60 * 60
/** Mainnet block time ~12s; historical block is estimated then the real
 *  elapsed time is read back from block timestamps. */
const BLOCK_TIME_SECONDS = 12
const WINDOW_BLOCKS = BigInt(Math.floor(WINDOW_SECONDS / BLOCK_TIME_SECONDS))

const MAX_RPC_TRIES = 5

const CONVERT_TO_ASSETS_ABI = [
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'shares' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

const readRateAt = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: any,
  blockNumber?: bigint,
): Promise<bigint> =>
  client.readContract({
    address: APXETH,
    abi: CONVERT_TO_ASSETS_ABI,
    functionName: 'convertToAssets',
    args: [ONE_E18],
    ...(blockNumber !== undefined ? { blockNumber } : {}),
  }) as Promise<bigint>

const computeApr = async (): Promise<number> => {
  let lastErr: unknown
  for (let rpcId = 0; rpcId < MAX_RPC_TRIES; rpcId++) {
    try {
      const client = getEvmClient(CHAIN_ID, rpcId)
      const head = await client.getBlockNumber()
      const pastBlock = head > WINDOW_BLOCKS ? head - WINDOW_BLOCKS : 0n

      const [rateNow, rateThen, headBlock, thenBlock] = await Promise.all([
        readRateAt(client),
        readRateAt(client, pastBlock),
        client.getBlock({ blockNumber: head }),
        client.getBlock({ blockNumber: pastBlock }),
      ])

      if (rateNow < ONE_E18 || rateThen < ONE_E18 || rateNow < rateThen) {
        throw new Error(
          `non-archival or invalid rates (now=${rateNow}, then=${rateThen})`,
        )
      }

      const elapsed = Number(headBlock.timestamp - thenBlock.timestamp)
      if (elapsed <= 0) throw new Error('non-positive elapsed window')

      const apr = annualizeRateDeltaPercent(rateNow, rateThen, elapsed)
      if (!Number.isFinite(apr) || apr < 0) throw new Error(`bad apr ${apr}`)
      return apr
    } catch (e) {
      lastErr = e
    }
  }
  throw new Error(
    `beraETH APR: no archival mainnet RPC returned valid apxETH state (${String(lastErr)})`,
  )
}

export const beraEthFetcher: YieldFetcher = {
  label: 'BERAETH',
  fetch: async () => {
    try {
      const apy = await fetchDefiLlamaApy(APXETH_DEFILLAMA_POOL)
      return { beraETH: apyToAprPercent(apy) }
    } catch {
      // DefiLlama unreachable — fall back to on-chain apxETH rate-delta.
      return { beraETH: await computeApr() }
    }
  },
}
